import { Check, Sparkle } from 'lucide-react'
import {
  type AnalysisCategory,
  type ExtractionPhase,
  type SweepProgress,
  getExtractionPhaseLabel,
} from '@/types/extraction'
import { cn } from '@/lib/utils'

const PHASES: ExtractionPhase[] = [
  'discovering',
  'ranking',
  'synthesizing',
  'verifying',
  'saving',
]

/**
 * Reassurance line shown only for async sweep runs. The sweep can run for
 * half an hour on a large tender, so the copy has to (a) set that expectation
 * up front and (b) tell the user the page isn't a leash — the completion
 * notification is what makes leaving safe.
 */
const SWEEP_REASSURANCE =
  "Analysing the full document set — this can take up to half an hour for large tenders. You can leave this page; we'll notify you when it's finished."

interface ExtractionPhaseStepperProps {
  /** Item-producing category, or `tender_overview` for the summary lane. */
  category: AnalysisCategory
  /** Latest reported phase, or null if the run hasn't reported yet. */
  phase: ExtractionPhase | null
  /**
   * Windows read / windows planned for an in-flight async sweep. Absent or
   * null for inline runs, and briefly null at the very start of a sweep (the
   * gap between run creation and the first heartbeat stamp) — so every use
   * below degrades to the plain phase label rather than rendering a partial
   * count.
   */
  sweepProgress?: SweepProgress | null
  /**
   * True while an async sweep owns this category. Drives the reassurance line
   * on its own — NOT via `sweepProgress` — because the promise it makes is
   * true for the whole run, including before the first count arrives and on
   * any cycle the server couldn't read progress.
   */
  isSweeping?: boolean
}

export function ExtractionPhaseStepper({
  category,
  phase,
  sweepProgress,
  isSweeping = false,
}: ExtractionPhaseStepperProps) {
  // Default the highlighted step to the first phase until the backend
  // reports otherwise — feels active immediately rather than blank.
  const activeIndex = phase ? PHASES.indexOf(phase) : 0

  // A zero-window plan is not a sweep we can narrate; treat it as absent so
  // the user never sees "0 of 0" or a stalled-looking counter.
  const progress = sweepProgress && sweepProgress.total > 0 ? sweepProgress : null

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      {/* Header — minimal frame strip. The active row carries the live signal. */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b bg-muted/40">
        <span className="text-meta tracking-wide">Analysing tender</span>
        <span className="text-meta tabular-nums">
          Step {Math.max(activeIndex, 0) + 1} of {PHASES.length}
        </span>
      </div>

      {/* Stepper rows — done / active / pending */}
      <div className="flex flex-col gap-1 px-2 py-3">
        {PHASES.map((p, i) => {
          const isDone = i < activeIndex
          const isActive = i === activeIndex
          // The sweep spends nearly all its time in `synthesizing`, so the
          // ACTIVE synthesising row is the only place a live count means
          // anything — it turns a frozen label into visible movement. A
          // still-pending row keeps its plain label; "0 of 28" on a step that
          // hasn't started would read as stalled.
          //
          // The unit is a PASS over the tender, not a file: windows are
          // ~27k-token overlapping slices, so one large document becomes
          // several and the denominator is always larger than the upload
          // count. Naming files here would hand the user a number they can
          // falsify at a glance. The wording also stays clear of step 1's
          // "Reading tender documents" so a mid-run stepper can't read as if
          // it had gone backwards and started over.
          const isProgressRow = Boolean(progress) && isActive && p === 'synthesizing'
          const label =
            progress && isProgressRow
              ? `Working through the tender (pass ${progress.done} of ${progress.total})`
              : getExtractionPhaseLabel(category, p).replace(/…$/, '')
          return (
            <div
              key={p}
              className={cn(
                'flex items-center gap-2.5 px-2 py-1.5 rounded-md',
                isActive && 'animate-active-row'
              )}
            >
              <Marker done={isDone} active={isActive} />
              <span
                className={cn(
                  'text-sm',
                  isDone && 'text-muted-foreground',
                  // Active label uses the shimmer text sweep so the user sees
                  // the active row "breathing" at the meaning, not just the
                  // background tint.
                  isActive && 'font-medium animate-shimmer',
                  !isDone && !isActive && 'text-muted-foreground',
                  // Fixed-width digits so the label doesn't jog sideways as
                  // the count rolls 9 → 10 (matches the header's step counter).
                  isProgressRow && 'tabular-nums'
                )}
              >
                {label}
              </span>
            </div>
          )
        })}
      </div>

      {/*
        Flat muted line inside the existing card — no second card, no border.
        Keyed on `isSweeping`, never on the counter: it must be there from the
        first paint of a sweep and must not vanish on a cycle where progress
        couldn't be read. `progress` is kept as a fallback so a sweep that
        somehow reports a count without the flag still explains itself.
      */}
      {(isSweeping || progress) && (
        <p className="text-meta px-4 pb-3">{SWEEP_REASSURANCE}</p>
      )}
    </div>
  )
}

function Marker({ done, active }: { done: boolean; active: boolean }) {
  if (done) {
    return (
      <span
        className="flex-shrink-0 inline-flex items-center justify-center h-3.5 w-3.5 rounded-full"
        style={{ background: 'hsl(var(--brand))' }}
        aria-hidden="true"
      >
        <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />
      </span>
    )
  }
  if (active) {
    // Solid iris sparkle — the row itself pulses (animate-active-row), so the
    // icon stays still and visible. Filled to maximize presence on white.
    return (
      <Sparkle
        className="flex-shrink-0 h-4 w-4 text-[hsl(var(--ai-accent))]"
        fill="hsl(var(--ai-accent))"
        aria-hidden="true"
      />
    )
  }
  // Pending: hollow neutral
  return (
    <span
      className="flex-shrink-0 inline-block h-3.5 w-3.5 rounded-full border-2 border-border bg-muted"
      aria-hidden="true"
    />
  )
}
